import { useState } from "react";
import { Link } from "react-router-dom";
import ProductCard from "@/components/base/ProductCard";
import ProductDetailModal from "@/components/base/ProductDetailModal";
import ReviewWriteModal from "@/components/base/ReviewWriteModal";
import { products, Product } from "@/mocks/products";
import { useProductImages } from "@/hooks/useProductImages";

const FeaturedProducts = () => {
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const { getImage, setImage, resetImage } = useProductImages();

  const featured = [
    ...products.filter((p) => p.badge === "BEST"),
    ...products.filter((p) => p.badge === "NEW"),
    ...products.filter((p) => !p.badge),
  ].slice(0, 8);

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
          <div>
            <p className="text-xs font-bold tracking-widest uppercase text-stone-400 mb-2">Best Sellers</p>
            <h2 className="text-3xl md:text-4xl font-black text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
              인기 제품
            </h2>
            <p className="text-sm text-stone-500 mt-2">도어준에서 가장 많이 선택받은 프리미엄 도어락</p>
          </div>
          <div className="flex items-center gap-3 self-start sm:self-auto flex-wrap">
            {/* 이미지 편집 모드 토글 */}
            <button
              onClick={() => setEditMode((v) => !v)}
              className={`inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full border transition-colors whitespace-nowrap cursor-pointer ${
                editMode
                  ? "bg-stone-900 text-white border-stone-900"
                  : "text-stone-700 border-stone-200 hover:bg-stone-50"
              }`}
            >
              <i className={`${editMode ? "ri-check-line" : "ri-image-edit-line"} text-base`} />
              {editMode ? "편집 완료" : "이미지 편집"}
            </button>
            <button
              onClick={() => setShowReviewModal(true)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-stone-700 border border-stone-200 px-4 py-2 rounded-full hover:bg-stone-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-edit-line text-base" />
              리뷰 작성하기
            </button>
            <Link
              to="/categories"
              className="text-sm font-medium text-stone-600 underline underline-offset-4 hover:text-stone-900 transition-colors whitespace-nowrap cursor-pointer"
            >
              전체 제품 보기 →
            </Link>
          </div>
        </div>

        {/* 편집 모드 안내 배너 */}
        {editMode && (
          <div className="mb-6 flex items-center gap-3 bg-stone-900 text-white rounded-xl px-5 py-3">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className="ri-information-line text-white/70" />
            </div>
            <p className="text-sm">
              <span className="font-bold">이미지 편집 모드</span>
              <span className="text-white/60 ml-2">제품 이미지에 마우스를 올리면 변경 버튼이 나타납니다. 이미지 URL을 입력해 교체하세요.</span>
            </p>
            <button
              onClick={() => setEditMode(false)}
              className="ml-auto w-6 h-6 flex items-center justify-center text-white/50 hover:text-white transition-colors cursor-pointer flex-shrink-0"
            >
              <i className="ri-close-line" />
            </button>
          </div>
        )}

        {/* Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-product-shop>
          {featured.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              onOpenDetail={setSelectedProduct}
              editMode={editMode}
              imageOverride={getImage(product.id, product.image) !== product.image ? getImage(product.id, product.image) : undefined}
              onImageChange={setImage}
              onImageReset={resetImage}
            />
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/categories"
            className="inline-flex items-center gap-2 border border-stone-200 text-stone-700 text-sm font-semibold px-8 py-3 rounded-full hover:bg-stone-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            전체 제품 보기
            <i className="ri-arrow-right-line" />
          </Link>
          <a
            href="https://open.kakao.com/o/srLtta1b"
            target="_blank"
            rel="nofollow noreferrer"
            className="inline-flex items-center gap-2 text-sm font-bold px-8 py-3 rounded-full whitespace-nowrap cursor-pointer transition-colors"
            style={{ backgroundColor: "#FEE500", color: "#3A1D1D" }}
          >
            <i className="ri-chat-smile-2-line" />
            카카오로 무료 견적 받기
          </a>
        </div>
      </div>

      {/* Product Detail Modal */}
      <ProductDetailModal product={selectedProduct} onClose={() => setSelectedProduct(null)} />

      {/* Review Write Modal */}
      {showReviewModal && <ReviewWriteModal onClose={() => setShowReviewModal(false)} />}
    </section>
  );
};

export default FeaturedProducts;
